import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, apiFetchJson } from '../../apiClient';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

function EsppTracker({ purchases, enrollments, currency, exchangeRate, currentPrice, instance, onRefresh }) {
  const { t } = useTranslation();
  const [showPurchaseForm, setShowPurchaseForm] = useState(false);
  const [showEnrollmentForm, setShowEnrollmentForm] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [generatedRows, setGeneratedRows] = useState([]);
  const [bulkCreating, setBulkCreating] = useState(false);
  const [purchaseForm, setPurchaseForm] = useState({
    purchaseDate: '',
    contributionUSD: '',
    marketPriceUSD: '',
    discountPercent: 10,
    exchangeRate: ''
  });
  const [enrollmentForm, setEnrollmentForm] = useState({
    contributionPercent: '',
    discountPercent: 10,
    startDate: ''
  });
  const [fetchingPrice, setFetchingPrice] = useState(false);

  // Fetch MSFT closing price for a specific date
  const fetchPriceForDate = async (dateStr) => {
    try {
      const data = await apiFetchJson(instance, `/api/StockPrice/date/MSFT?date=${dateStr}`);
      return data.priceUSD;
    } catch (err) {
      console.error('Failed to fetch price for', dateStr, err);
    }
    return null;
  };

  // Fetch USD/NOK exchange rate for a specific date
  const fetchExchangeRateForDate = async (dateStr) => {
    try {
      const res = await apiFetch(instance, `/api/StockPrice/exchange-rate/history?from=USD&to=NOK&startDate=${dateStr}&endDate=${dateStr}`);
      if (res.ok) {
        const data = await res.json();
        const rates = Array.isArray(data) ? data : (data?.$values || []);
        if (rates.length > 0) return rates[0].rate;
      }
      // Fallback: get current rate
      const res2 = await apiFetch(instance, '/api/StockPrice/exchange-rate?from=USD&to=NOK');
      if (res2.ok) {
        const data2 = await res2.json();
        return data2.rate;
      }
    } catch (err) {
      console.error('Failed to fetch exchange rate for', dateStr, err);
    }
    return null;
  };

  // Auto-fetch price and exchange rate when user picks a date in the manual purchase form
  const handlePurchaseDateChange = async (dateStr) => {
    setPurchaseForm(f => ({ ...f, purchaseDate: dateStr }));
    if (dateStr) {
      setFetchingPrice(true);
      const [price, rate] = await Promise.all([
        fetchPriceForDate(dateStr),
        fetchExchangeRateForDate(dateStr)
      ]);
      setPurchaseForm(f => ({
        ...f,
        marketPriceUSD: price != null ? price.toString() : f.marketPriceUSD,
        exchangeRate: rate != null ? rate.toString() : f.exchangeRate
      }));
      setFetchingPrice(false);
    }
  };

  const formatCurrency = (usd) => {
    if (usd == null) return '—';
    if (currency === 'NOK' && exchangeRate) {
      return `${(usd * exchangeRate).toLocaleString('nb-NO', { maximumFractionDigits: 0 })} kr`;
    }
    return `$${usd.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  };

  const handlePurchaseSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiFetchJson(instance, '/api/Espp/purchases', {
        method: 'POST',
        body: JSON.stringify({
          purchaseDate: purchaseForm.purchaseDate,
          contributionUSD: parseFloat(purchaseForm.contributionUSD),
          marketPriceUSD: parseFloat(purchaseForm.marketPriceUSD),
          discountPercent: parseFloat(purchaseForm.discountPercent),
          exchangeRate: purchaseForm.exchangeRate ? parseFloat(purchaseForm.exchangeRate) : null
        })
      });
      setShowPurchaseForm(false);
      setPurchaseForm({ purchaseDate: '', contributionUSD: '', marketPriceUSD: '', discountPercent: 10, exchangeRate: '' });
      onRefresh();
    } catch (err) {
      console.error('Failed to create ESPP purchase:', err);
    }
  };

  const handleEnrollmentSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiFetchJson(instance, '/api/Espp/enrollments', {
        method: 'POST',
        body: JSON.stringify({
          contributionPercent: parseFloat(enrollmentForm.contributionPercent),
          discountPercent: parseFloat(enrollmentForm.discountPercent),
          startDate: enrollmentForm.startDate
        })
      });
      setShowEnrollmentForm(false);
      setEnrollmentForm({ contributionPercent: '', discountPercent: 10, startDate: '' });
      onRefresh();
    } catch (err) {
      console.error('Failed to create ESPP enrollment:', err);
    }
  };

  const handleDeletePurchase = async (id) => {
    if (!window.confirm(t('compensation.deleteEsppConfirm'))) return;
    try {
      await apiFetchJson(instance, `/api/Espp/purchases/${id}`, { method: 'DELETE' });
      onRefresh();
    } catch (err) {
      console.error('Failed to delete ESPP purchase:', err);
    }
  };

  const handleDeleteEnrollment = async (id) => {
    if (!window.confirm(t('compensation.deleteEnrollmentConfirm'))) return;
    try {
      await apiFetchJson(instance, `/api/Espp/enrollments/${id}`, { method: 'DELETE' });
      onRefresh();
    } catch (err) {
      console.error('Failed to delete ESPP enrollment:', err);
    }
  };

  // Generate missing quarterly purchases from backend
  const generateMissingPurchases = async () => {
    try {
      const data = await apiFetchJson(instance, '/api/Espp/generate-missing');
      const arr = Array.isArray(data) ? data : (data?.$values || []);

      const rows = arr.map(r => ({
        purchaseDate: r.purchaseDate?.split('T')[0] || '',
        monthCount: r.monthCount,
        contributionNOK: Math.round(r.contributionNOK || 0),
        discountPercent: r.discountPercent || 10,
        contributionUSD: r.contributionUSD ? r.contributionUSD.toString() : '',
        marketPriceUSD: r.marketPriceUSD ? r.marketPriceUSD.toString() : '',
        exchangeRate: r.exchangeRate ? r.exchangeRate.toString() : ''
      }));

      setGeneratedRows(rows);
      setShowGenerate(true);
    } catch (err) {
      console.error('Failed to generate missing purchases:', err);
    }
  };

  const handleBulkCreate = async () => {
    const toCreate = generatedRows.filter(r => r.marketPriceUSD && r.contributionUSD);
    if (toCreate.length === 0) return;

    setBulkCreating(true);
    let created = 0;

    for (const row of toCreate) {
      try {
        await apiFetchJson(instance, '/api/Espp/purchases', {
          method: 'POST',
          body: JSON.stringify({
            purchaseDate: row.purchaseDate,
            contributionUSD: parseFloat(row.contributionUSD),
            marketPriceUSD: parseFloat(row.marketPriceUSD),
            discountPercent: parseFloat(row.discountPercent),
            exchangeRate: row.exchangeRate ? parseFloat(row.exchangeRate) : null
          })
        });
        created++;
      } catch (err) {
        console.error('Failed to create purchase for', row.purchaseDate, err);
      }
    }

    setBulkCreating(false);
    if (created > 0) {
      setShowGenerate(false);
      setGeneratedRows([]);
      onRefresh();
    }
  };

  const canGenerate = enrollments.length > 0;
  const filledCount = generatedRows.filter(r => r.marketPriceUSD && r.contributionUSD && r.exchangeRate).length;

  // Summary calculations
  const totalShares = purchases.reduce((sum, p) => sum + (p.sharesPurchased || 0), 0);
  const totalContribution = purchases.reduce((sum, p) => sum + (p.contributionUSD || 0), 0);
  const totalDiscountGain = purchases.reduce((sum, p) => sum + (p.discountGainUSD || 0), 0);
  const currentValue = currentPrice ? totalShares * currentPrice : null;
  const totalGain = currentValue != null ? currentValue - totalContribution : null;

  // Current enrollment
  const currentEnrollment = enrollments.find(e => !e.endDate);

  // Build cumulative chart data sorted by purchase date
  const sorted = [...purchases].sort((a, b) => new Date(a.purchaseDate) - new Date(b.purchaseDate));
  const chartData = [];
  let cumShares = 0;
  let cumCost = 0;
  let cumDiscount = 0;
  sorted.forEach(p => {
    cumShares += p.sharesPurchased || 0;
    cumCost += p.contributionUSD || 0;
    cumDiscount += p.discountGainUSD || 0;
    const cumValueUSD = currentPrice ? cumShares * currentPrice : cumCost;
    chartData.push({
      date: new Date(p.purchaseDate).toLocaleDateString('nb-NO', { month: 'short', year: '2-digit' }),
      shares: parseFloat(cumShares.toFixed(2)),
      costUSD: parseFloat(cumCost.toFixed(0)),
      valueUSD: parseFloat(cumValueUSD.toFixed(0)),
      gainUSD: parseFloat((cumValueUSD - cumCost).toFixed(0)),
      discountUSD: parseFloat(cumDiscount.toFixed(0))
    });
  });

  const currLabel = currency === 'NOK' ? 'kr' : '$';
  const convertChart = (v) => currency === 'NOK' && exchangeRate ? Math.round(v * exchangeRate) : v;

  return (
    <div className="espp-tracker">
      {/* Enrollment Section */}
      <div className="espp-enrollment-section">
        <div className="espp-header">
          <h3>{t('compensation.esppEnrollment')}</h3>
          <button className="btn-primary" onClick={() => setShowEnrollmentForm(!showEnrollmentForm)}>
            {currentEnrollment ? t('compensation.changeContribution') : t('compensation.startEspp')}
          </button>
        </div>

        {currentEnrollment && (
          <div className="espp-current-enrollment">
            <span className="enrollment-badge active">● {t('compensation.active')}</span>
            <span><strong>{currentEnrollment.contributionPercent}%</strong> {t('compensation.ofSalary')}</span>
            <span>{currentEnrollment.discountPercent}% {t('compensation.discount')}</span>
            <span>{t('compensation.since')} {new Date(currentEnrollment.startDate).toLocaleDateString('nb-NO')}</span>
          </div>
        )}
        {!currentEnrollment && !showEnrollmentForm && (
          <div className="espp-no-enrollment">{t('compensation.noActiveEspp')}</div>
        )}

        {showEnrollmentForm && (
          <form className="espp-form" onSubmit={handleEnrollmentSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label>{t('compensation.contributionPercent')}</label>
                <input type="number" value={enrollmentForm.contributionPercent}
                  onChange={e => setEnrollmentForm(f => ({ ...f, contributionPercent: e.target.value }))}
                  min="0" max="100" step="0.5" placeholder="15" required />
              </div>
              <div className="form-group">
                <label>{t('compensation.discount')} %</label>
                <input type="number" value={enrollmentForm.discountPercent}
                  onChange={e => setEnrollmentForm(f => ({ ...f, discountPercent: e.target.value }))}
                  min="0" max="50" step="0.5" />
              </div>
              <div className="form-group">
                <label>{t('compensation.startDate')}</label>
                <input type="date" value={enrollmentForm.startDate}
                  onChange={e => setEnrollmentForm(f => ({ ...f, startDate: e.target.value }))} required />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary">{t('common.save')}</button>
              <button type="button" className="btn-secondary" onClick={() => setShowEnrollmentForm(false)}>{t('common.cancel')}</button>
            </div>
          </form>
        )}

        {/* Enrollment history */}
        {enrollments.length > 0 && (
          <div className="enrollment-history">
            <h4>{t('compensation.enrollmentHistory')}</h4>
            <table className="espp-table">
              <thead>
                <tr>
                  <th>{t('compensation.contributionPercent')}</th>
                  <th>{t('compensation.discount')}</th>
                  <th>{t('compensation.startDate')}</th>
                  <th>{t('compensation.endDate')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {enrollments.map(e => (
                  <tr key={e.id} className={!e.endDate ? 'active-row' : ''}>
                    <td>{e.contributionPercent}%</td>
                    <td>{e.discountPercent}%</td>
                    <td>{new Date(e.startDate).toLocaleDateString('nb-NO')}</td>
                    <td>{e.endDate ? new Date(e.endDate).toLocaleDateString('nb-NO') : '—'}</td>
                    <td>
                      <button className="btn-icon" onClick={() => handleDeleteEnrollment(e.id)}>🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Purchases Section */}
      <div className="espp-purchases-section">
        <div className="espp-header">
          <h3>{t('compensation.esppPurchases')}</h3>
          <div className="espp-header-actions">
            {canGenerate && (
              <button className="btn-secondary" onClick={generateMissingPurchases}>
                🔄 {t('compensation.generatePurchases')}
              </button>
            )}
            <button className="btn-primary" onClick={() => setShowPurchaseForm(!showPurchaseForm)}>
              + {t('compensation.addPurchase')}
            </button>
          </div>
        </div>

        {/* Generate panel */}
        {showGenerate && (
          <div className="espp-generate-panel">
            <h4>{t('compensation.generateTitle')}</h4>
            <p className="generate-description">{t('compensation.generateDescription')}</p>
            {generatedRows.length === 0 ? (
              <p className="generate-empty">{t('compensation.generateEmpty')}</p>
            ) : (
              <>
                <div className="espp-table-container">
                  <table className="espp-table generate-table">
                    <thead>
                      <tr>
                        <th>{t('compensation.date')}</th>
                        <th>{t('compensation.months')}</th>
                        <th>NOK</th>
                        <th>USD</th>
                        <th>MSFT $</th>
                        <th>{t('compensation.discount')}</th>
                        <th>USD/NOK</th>
                      </tr>
                    </thead>
                    <tbody>
                      {generatedRows.map((row, i) => (
                        <tr key={row.purchaseDate} className={row.marketPriceUSD && row.contributionUSD ? 'row-ready' : 'row-missing'}>
                          <td>{new Date(row.purchaseDate + 'T12:00:00').toLocaleDateString('nb-NO')}</td>
                          <td className="center-cell">{row.monthCount}</td>
                          <td className="amount-cell">{row.contributionNOK.toLocaleString('nb-NO')} kr</td>
                          <td className="amount-cell">{row.contributionUSD ? `$${parseFloat(row.contributionUSD).toLocaleString('en-US', { maximumFractionDigits: 2 })}` : '—'}</td>
                          <td className="amount-cell">{row.marketPriceUSD ? `$${parseFloat(row.marketPriceUSD).toLocaleString('en-US', { maximumFractionDigits: 2 })}` : '—'}</td>
                          <td className="center-cell">{row.discountPercent}%</td>
                          <td className="amount-cell">{row.exchangeRate ? parseFloat(row.exchangeRate).toFixed(2) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="generate-hint">{t('compensation.generateAutoHint')}</p>
                <div className="generate-actions">
                  <button className="btn-primary" onClick={handleBulkCreate}
                    disabled={bulkCreating || filledCount === 0}>
                    {bulkCreating ? t('common.saving') : `${t('compensation.createPurchases')} (${filledCount})`}
                  </button>
                  <button className="btn-secondary" onClick={() => setShowGenerate(false)}>
                    {t('common.cancel')}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Summary */}
        <div className="espp-summary">
          <div className="stat">
            <span className="stat-label">{t('compensation.totalContributed')}</span>
            <span className="stat-value">{formatCurrency(totalContribution)}</span>
          </div>
          <div className="stat">
            <span className="stat-label">{t('compensation.totalShares')}</span>
            <span className="stat-value">{totalShares.toFixed(2)}</span>
          </div>
          <div className="stat">
            <span className="stat-label">{t('compensation.discountGain')}</span>
            <span className="stat-value" style={{ color: 'var(--success-500)' }}>{formatCurrency(totalDiscountGain)}</span>
          </div>
          {currentValue != null && (
            <div className="stat">
              <span className="stat-label">{t('compensation.currentValue')}</span>
              <span className="stat-value">{formatCurrency(currentValue)}</span>
            </div>
          )}
          {totalGain != null && (
            <div className="stat" style={{ color: totalGain >= 0 ? 'var(--success-500)' : 'var(--danger-500)' }}>
              <span className="stat-label">{t('compensation.totalGain')}</span>
              <span className="stat-value">{formatCurrency(totalGain)}</span>
            </div>
          )}
        </div>

        {/* ESPP Growth Chart */}
        {chartData.length > 1 && (
          <div className="espp-chart-section">
            <h4>{t('compensation.esppGrowth')}</h4>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--surface-border)" />
                <XAxis dataKey="date" stroke="var(--text-muted)" />
                <YAxis stroke="var(--text-muted)"
                  tickFormatter={v => `${currLabel}${v >= 1000 ? `${(convertChart(v) / 1000).toFixed(0)}k` : convertChart(v)}`} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'var(--surface-card)', border: '1px solid var(--surface-border)', borderRadius: '8px' }}
                  labelStyle={{ color: 'var(--text-primary)' }}
                  formatter={(value, name) => [
                    `${currLabel}${convertChart(value).toLocaleString()}`,
                    name
                  ]}
                />
                <Legend />
                <Area type="monotone" dataKey="costUSD" name={t('compensation.totalContributed')}
                  fill="#3b82f6" fillOpacity={0.15} stroke="#3b82f6" strokeWidth={2} />
                <Area type="monotone" dataKey="valueUSD" name={t('compensation.currentValue')}
                  fill="#10b981" fillOpacity={0.15} stroke="#10b981" strokeWidth={2} />
                <Area type="monotone" dataKey="discountUSD" name={t('compensation.discountGain')}
                  fill="#8b5cf6" fillOpacity={0.15} stroke="#8b5cf6" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>

            {/* All-time totals */}
            <div className="espp-totals">
              <div className="espp-total-item">
                <span className="espp-total-label">{t('compensation.totalShares')}</span>
                <span className="espp-total-value">{totalShares.toFixed(4)}</span>
              </div>
              <div className="espp-total-item">
                <span className="espp-total-label">{t('compensation.totalContributed')}</span>
                <span className="espp-total-value">{formatCurrency(totalContribution)}</span>
                {currency === 'USD' && exchangeRate && (
                  <span className="espp-total-sub">{(totalContribution * exchangeRate).toLocaleString('nb-NO', { maximumFractionDigits: 0 })} kr</span>
                )}
              </div>
              {currentValue != null && (
                <div className="espp-total-item">
                  <span className="espp-total-label">{t('compensation.currentValue')}</span>
                  <span className="espp-total-value">{formatCurrency(currentValue)}</span>
                  {currency === 'USD' && exchangeRate && (
                    <span className="espp-total-sub">{(currentValue * exchangeRate).toLocaleString('nb-NO', { maximumFractionDigits: 0 })} kr</span>
                  )}
                </div>
              )}
              {totalGain != null && (
                <div className="espp-total-item" style={{ color: totalGain >= 0 ? 'var(--success-500)' : 'var(--danger-500)' }}>
                  <span className="espp-total-label">{t('compensation.totalGain')}</span>
                  <span className="espp-total-value">
                    {totalGain >= 0 ? '+' : ''}{formatCurrency(totalGain)}
                    {totalContribution > 0 && ` (${totalGain >= 0 ? '+' : ''}${((totalGain / totalContribution) * 100).toFixed(1)}%)`}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Add purchase form */}
        {showPurchaseForm && (
          <form className="espp-form" onSubmit={handlePurchaseSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label>{t('compensation.purchaseDate')}</label>
                <input type="date" value={purchaseForm.purchaseDate} onChange={e => handlePurchaseDateChange(e.target.value)} required />
                {fetchingPrice && <span className="auto-fetch-hint">⏳ {t('compensation.fetchingData')}</span>}
              </div>
              <div className="form-group">
                <label>{t('compensation.contribution')} (USD)</label>
                <input type="number" value={purchaseForm.contributionUSD} onChange={e => setPurchaseForm(f => ({ ...f, contributionUSD: e.target.value }))}
                  placeholder="e.g. 3000" step="0.01" required />
              </div>
              <div className="form-group">
                <label>{t('compensation.marketPrice')} (USD)</label>
                <input type="number" value={purchaseForm.marketPriceUSD} onChange={e => setPurchaseForm(f => ({ ...f, marketPriceUSD: e.target.value }))}
                  placeholder="e.g. 420.00" step="0.01" required />
              </div>
              <div className="form-group">
                <label>{t('compensation.discount')} %</label>
                <input type="number" value={purchaseForm.discountPercent} onChange={e => setPurchaseForm(f => ({ ...f, discountPercent: e.target.value }))}
                  min="0" max="50" step="0.5" />
              </div>
              <div className="form-group">
                <label>USD/NOK</label>
                <input type="number" value={purchaseForm.exchangeRate} onChange={e => setPurchaseForm(f => ({ ...f, exchangeRate: e.target.value }))}
                  placeholder={t('compensation.optional')} step="0.01" />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary">{t('common.save')}</button>
              <button type="button" className="btn-secondary" onClick={() => setShowPurchaseForm(false)}>{t('common.cancel')}</button>
            </div>
          </form>
        )}

        {/* Purchase table */}
        <div className="espp-table-container">
          <table className="espp-table">
            <thead>
              <tr>
                <th>{t('compensation.date')}</th>
                <th>{t('compensation.contribution')}</th>
                <th>{t('compensation.marketPrice')}</th>
                <th>{t('compensation.purchasePrice')}</th>
                <th>{t('compensation.shares')}</th>
                <th>{t('compensation.discountGain')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {purchases.map(p => (
                <tr key={p.id}>
                  <td>{new Date(p.purchaseDate).toLocaleDateString('nb-NO')}</td>
                  <td>{formatCurrency(p.contributionUSD)}</td>
                  <td>${p.marketPriceUSD?.toFixed(2)}</td>
                  <td>${p.purchasePriceUSD?.toFixed(2)}</td>
                  <td>{p.sharesPurchased?.toFixed(4)}</td>
                  <td style={{ color: 'var(--success-500)' }}>{formatCurrency(p.discountGainUSD)}</td>
                  <td>
                    <button className="btn-icon" onClick={() => handleDeletePurchase(p.id)}>🗑️</button>
                  </td>
                </tr>
              ))}
              {purchases.length === 0 && (
                <tr><td colSpan="7" className="empty-cell">{t('compensation.noEsppPurchases')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default EsppTracker;
